import { Alert, AlertTitle, Box, Button, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { CheckCircleOutlineOutlined, DescriptionOutlined, ErrorOutlineOutlined } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { GenerationStatus, goBack, resetState } from './document-generation-new-document.slice';
import DynamicForm from './DynamicForm';
import SelectTemplate from './SelectTemplate';
import SelectBuildings from './SelectBuildings';

export default function DocumentGenerationNewDocumentPage() {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { activeStep, generationStatus, generationResponse, generationError, validationErrors, errorMessage } =
        useAppSelector((state) => state.documentGenerationNewDocument);

    // Reset the state when the component unmounts
    // This ensures that the state is clean when navigating back to this page
    useEffect(() => {
        return () => {
            dispatch(resetState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (errorMessage) {
            dispatch(showSnackbar({ message: errorMessage, severity: NotificationSeverity.Error }));
        }
    }, [errorMessage, dispatch]);

    return (
        <>
            <Typography variant="h4" gutterBottom>
                {t('Generate Document')}
            </Typography>

            <Stepper activeStep={activeStep}>
                <Step key={0}>
                    <StepLabel>{t('Select a template')}</StepLabel>
                </Step>
                <Step key={1}>
                    <StepLabel>{t('Select Buildings')}</StepLabel>
                </Step>
                <Step key={2}>
                    <StepLabel>{t('Fill the form')}</StepLabel>
                </Step>
                <Step key={3}>
                    <StepLabel>{t('Download the document')}</StepLabel>
                </Step>
            </Stepper>

            {activeStep === 0 && <SelectTemplate />}
            {activeStep === 1 && (
                <>
                    <SelectBuildings />
                </>
            )}
            {activeStep === 2 && (
                <>
                    <DynamicForm />
                </>
            )}
            {activeStep === 3 && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    {generationStatus === GenerationStatus.GENERATING && (
                        <Typography variant="body1">{t('The document is being generated. Please wait...')}</Typography>
                    )}
                    {generationStatus === GenerationStatus.SUCCESS && (
                        <Typography variant="body1">
                            <Trans t={t}>
                                The document has been generated successfully. You can find it{' '}
                                <a href={generationResponse?.generatedUrl} target="_blank" rel="noopener noreferrer">
                                    here
                                </a>
                                .
                            </Trans>
                        </Typography>
                    )}
                    {generationStatus === GenerationStatus.FAILED && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Alert severity="error" sx={{ mt: 2, width: 600, textAlign: 'left' }}>
                                {validationErrors && (
                                    <>
                                        <AlertTitle>{t('Validation errors')}</AlertTitle>

                                        <ul>
                                            {validationErrors?.map((error, i) => (
                                                <li key={i}>
                                                    <strong>{error.parameter}:</strong> {error.detail}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {generationError && (
                                    <>
                                        <AlertTitle>{t('Generation failed')}</AlertTitle>
                                        <Typography variant="body1">
                                            {generationError.errorCode === 'TEMPLATE_ERROR' &&
                                                t('There is a problem with the template:')}
                                            {generationError.errorCode === 'DOCUMENT_PATH_NOT_FOUND' &&
                                                t('The document path or template path could not be found:')}
                                            {' ' + generationError.detail}
                                        </Typography>
                                    </>
                                )}
                            </Alert>
                        </Box>
                    )}

                    <Box sx={{ position: 'relative', display: 'inline-flex', mt: 3, mb: 2 }}>
                        <CircularProgress
                            size="8rem"
                            color={
                                generationStatus === GenerationStatus.GENERATING
                                    ? 'primary'
                                    : generationStatus === GenerationStatus.SUCCESS
                                      ? 'success'
                                      : 'error'
                            }
                            variant={generationStatus === GenerationStatus.GENERATING ? 'indeterminate' : 'determinate'}
                            value={generationStatus !== GenerationStatus.GENERATING ? 100 : undefined}
                        />
                        <Box
                            sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {generationStatus === GenerationStatus.GENERATING && (
                                <DescriptionOutlined fontSize="large" color="primary" />
                            )}
                            {generationStatus === GenerationStatus.SUCCESS && (
                                <CheckCircleOutlineOutlined fontSize="large" color="success" />
                            )}
                            {generationStatus === GenerationStatus.FAILED && (
                                <ErrorOutlineOutlined fontSize="large" color="error" />
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {activeStep > 0 && (
                <Button sx={{ mt: 1 }} variant="outlined" onClick={() => dispatch(goBack())}>
                    {t('Back')}
                </Button>
            )}
        </>
    );
}
